import { useQuery } from "@tanstack/react-query";
import { Download, RefreshCw, Search } from "lucide-react";
import { useCallback, useState } from "react";
import { toast } from "sonner";
import { ReauthDialog } from "@/components/shared/ReauthDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useReauth } from "@/hooks/use-reauth";
import { apiFetch } from "@/lib/api";
import { auditKeys } from "@/lib/query";

interface AuditEvent {
  id: string;
  createdAt: string;
  action: string;
  entityType: string;
  entityId: string;
  actorUserId: string | null;
  actorName: string | null;
  actorIp: string | null;
  userAgent: string | null;
  projectId: string | null;
  metadata: unknown;
}

interface AuditTrailResponse {
  data: AuditEvent[];
  nextCursor: string | null;
  hasMore: boolean;
}

const ACTION_VARIANT: Record<string, "success" | "destructive" | "secondary" | "default"> = {
  "auth.login.success": "success",
  "auth.reauth.success": "success",
  "auth.password.reset": "default",
  "auth.login.failure": "destructive",
  "auth.reauth.failure": "destructive",
  "user.deactivated": "destructive",
};

function actionVariant(action: string) {
  return ACTION_VARIANT[action] ?? "secondary";
}

export function AuditTrailTable() {
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState(false);
  const { dialogOpen, onDialogOpenChange, withReauth, onVerified } = useReauth();

  const filters = {
    ...(search ? { action: search } : {}),
    ...(userId ? { userId } : {}),
    ...(cursor ? { cursor } : {}),
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: auditKeys.trail(filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("action", search);
      if (userId) params.set("userId", userId);
      if (cursor) params.set("cursor", cursor);
      params.set("limit", "50");
      return apiFetch<AuditTrailResponse>(`/audit/trail?${params.toString()}`);
    },
  });

  const handleExport = useCallback(() => {
    withReauth(async (token) => {
      setExportLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set("action", search);
        if (userId) params.set("userId", userId);
        const res = await fetch(`/api/audit/export?${params.toString()}`, {
          headers: { "X-Reauth-Token": token },
        });
        if (res.status === 401) {
          toast.error("Re-authentication required. Please try again.");
          return;
        }
        if (!res.ok) {
          toast.error("Failed to export audit trail.");
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Audit trail exported");
      } catch {
        toast.error("Export failed.");
      } finally {
        setExportLoading(false);
      }
    });
  }, [withReauth, search, userId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            placeholder="Search actions..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCursor(undefined);
            }}
            className="pl-8"
          />
        </div>
        <Input
          placeholder="Filter by user ID..."
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setCursor(undefined);
          }}
          className="w-56"
        />
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="mr-1.5 size-3.5" />
          Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exportLoading}>
          <Download className="mr-1.5 size-3.5" />
          Export CSV
        </Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>IP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              ["r1", "r2", "r3", "r4", "r5", "r6", "r7", "r8"].map((rowKey) => (
                <TableRow key={rowKey}>
                  {["c1", "c2", "c3", "c4", "c5"].map((colKey) => (
                    <TableCell key={colKey}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (data?.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                  No audit events found
                </TableCell>
              </TableRow>
            ) : (
              (data?.data ?? []).map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(event.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm">
                    {event.actorName ?? event.actorUserId ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={actionVariant(event.action)} className="font-mono text-xs">
                      {event.action}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <span className="font-medium">{event.entityType}</span>
                    {event.entityId && (
                      <span className="ml-1 opacity-60">/{event.entityId.slice(0, 8)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {event.actorIp ?? "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data?.hasMore && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCursor(data.nextCursor ?? undefined)}
          >
            Load more
          </Button>
        </div>
      )}

      <ReauthDialog open={dialogOpen} onOpenChange={onDialogOpenChange} onVerified={onVerified} />
    </div>
  );
}
